import React from 'react';
import { Box } from '@wix/design-system';
import classNames from 'classnames';
import {
  AiAnalyzingBorder,
  useAiBorderMode,
  useCurrentModule,
  useEffectiveRecommendation,
  useAiRecommendationEnabled,
  ECLIPSE_PRIMARY_COLOR,
  ECLIPSE_BORDER_COLOR,
  useSelectButton,
  useProductHat,
} from '@wix/package-picker-shared-eclipse';
import { ProductCardList } from '@wix/premium-purchase-platform/@components';
import { CardContent } from './CardContent';
import s from './ProductCard.scss';

interface BaseProductCardProps {
  productId: string;
  cardIndex?: number;
  cardCount?: number;
}

export const BaseProductCard = ({
  productId,
  cardIndex = 0,
  cardCount,
}: BaseProductCardProps) => {
  const module = useCurrentModule();
  const { renderProductHat } = useProductHat({ productId });
  const { data: product } =
    module.SDK.eclipseOfferingApiClient.hooks.useFetchProduct({ productId });
  const { name } = product ?? {};
  const { SelectButton } = useSelectButton({ productId });
  const { isEnabled: isAiRecommendationEnabled, isPremiumPlanVariant } =
    useAiRecommendationEnabled();
  const { mode: borderMode, hidesCardBorder } = useAiBorderMode({
    productId,
    fadesUnpickedCards: isPremiumPlanVariant,
  });
  const { isRecommended } = useEffectiveRecommendation(
    productId,
    product?.isRecommended ?? false,
  );

  React.useEffect(() => {
    module.SDK.aiRecommendationStore.markPlansRendered();
  }, [module]);

  const borderColor = hidesCardBorder
    ? 'transparent'
    : isRecommended
    ? ECLIPSE_PRIMARY_COLOR
    : ECLIPSE_BORDER_COLOR;
  const borderWidth =
    isAiRecommendationEnabled || !isRecommended ? '1px' : '2px';
  const borderValue = `${borderWidth} solid ${borderColor}`;

  return (
    <ProductCardList.Default.Card.Root
      width="100%"
      paddingLeft="20px"
      paddingRight="20px"
      paddingBottom="40px"
      maxWidth="370px"
      minHeight="450px"
      borderRadius="16px"
      borderTop={borderValue}
      borderBottom={borderValue}
      borderLeft={borderValue}
      borderRight={borderValue}
      className={
        isAiRecommendationEnabled
          ? classNames(s.cardRoot, {
              [s.cardRecommended]: isRecommended,
            })
          : undefined
      }
    >
      {isAiRecommendationEnabled && (
        <AiAnalyzingBorder
          mode={borderMode}
          cardIndex={cardIndex}
          cardCount={cardCount}
          variant={isPremiumPlanVariant ? 'orbit' : 'comet'}
        />
      )}
      <Box
        width="100%"
        marginTop="SP3"
        marginBottom="SP4"
        className={s.productHatWrapper}
      >
        {renderProductHat()}
      </Box>
      <Box
        width="100%"
        direction="vertical"
        boxSizing="border-box"
        flexGrow={1}
      >
        <CardContent
          name={name}
          isRecommended={isRecommended}
          SelectButton={SelectButton}
          productId={productId}
        />
      </Box>
    </ProductCardList.Default.Card.Root>
  );
};
